// The `/chrome-extension/extension/**` route table (plan §7 phase 3).
//
// ⚠️ SLICE 1 SERVES THE CHROME, NOT THE SCREENS. Every route below renders `null` into the
// content view on purpose: the chrome around it — header, product name, tab strip, the flex
// container they all live in — is what slice 1 delivers, and the screens that fill the middle
// are slices 2 to 6.
//
// They are declared rather than left to `otherwise` because the two are not the same thing.
// An unclaimed path bounces to `/auth/status` via upstream's `otherwise`, which would be wrong
// for a route that exists and is merely unfinished; declaring it renders the real chrome at
// the real URL, which is what makes `pnpm baseline:compare` say anything useful about slice 1
// at all. None of them are added to `manifest.ts`'s `paths` — they are still owed by phase-3,
// and claiming parity for a screen with no content would be exactly the kind of false green
// the parity gate exists to prevent.
import type { ReactElement } from 'react'
import type { RouteObject } from 'react-router-dom'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { ExtensionContact, ProfileNotFound } from '@woo-ext/ui'
import { AddToJobRoute } from './AddToJobRoute.tsx'
import { CandidateMessagesRoute } from './CandidateMessagesRoute.tsx'
import { CandidateResumeRoute } from './CandidateResumeRoute.tsx'
import { ClientContactPreviewRoute } from './ClientContactPreviewRoute.tsx'
import { CandidateProfile } from './CandidateProfile.tsx'
import { ClientContactEditAlert } from './ClientContactEditAlert.tsx'
import { ClientContactProfile } from './ClientContactProfile.tsx'
import { ClientContactReview } from './ClientContactReview.tsx'
import { EditAlert } from './EditAlert.tsx'
import { EmailSignInRoute } from './EmailSignInRoute.tsx'
import { ExportPanelRoute } from './ExportPanelRoute.tsx'
import { ProfileExportRoute } from './ProfileExportRoute.tsx'
import { ShortlistReview } from './ShortlistReview.tsx'

/**
 * The content view, empty until its slice lands.
 *
 * Deliberately `null` and not a placeholder: a "coming soon" box would be UI this port
 * invented, it would land in the middle of every pixel diff for these routes, and it would
 * have to be removed again. An empty flex child is what an unfilled `ui-view` renders as.
 */
function PendingSlice(): null {
  return null
}

/** Paths relative to `/chrome-extension/extension`, from `routes.json`. */
const SHORTLIST_PATHS = [
  'shortlist/contact',
  'shortlist/edit-alert',
  'shortlist/profile-not-found',
  'shortlist/review',
  'shortlist/review/add-to-job',
  'shortlist/review/candidate-messages',
  'shortlist/review/candidate/profile',
  'shortlist/review/candidate/profile/more',
  'shortlist/review/email-signin',
  'shortlist/review/export',
  'shortlist/candidate/profile',
  'shortlist/candidate/profile/add-to-job',
  'shortlist/candidate/profile/candidate-messages',
  'shortlist/candidate/profile/email-signin',
  'shortlist/candidate/profile/export',
  'shortlist/candidate/profile/more',
  'shortlist/external-source-review',
  'shortlist/external-source-review/candidate/external-source-profile',
  'shortlist/external-source-review/external-source-add-to-job',
]

/**
 * The client-contact branch renders the same chrome with a different product name and tab
 * group — which is the Phase-4 design constraint the plan asks for, arriving for free here
 * because `ExtensionTabs` and `extensionChrome.ts` are written over the group rather than
 * over "candidates". Phase 4 fills these; slice 1 already gets their chrome right.
 */
const CLIENT_CONTACT_PATHS = [
  'client-contact/contact',
  'client-contact/edit-alert',
  'client-contact/profile-not-found',
  'client-contact/review',
  'client-contact/review/client-contact-messages',
  'client-contact/review/client-contact/profile',
  'client-contact/review/client-contact/profile/more',
  'client-contact/review/email-signin',
  'client-contact/review/export',
  'client-contact/client-contact/profile',
  'client-contact/client-contact/profile/client-contact-messages',
  'client-contact/client-contact/profile/email-signin',
  'client-contact/client-contact/profile/export',
  'client-contact/client-contact/profile/more',
]

/** Routes whose content view slice 2 has begun filling. */
const SERVED: Record<string, ReactElement> = {
  'shortlist/review': <ShortlistReview />,
  // ⚠️ THE SAME SCREEN, NOT A SIBLING OF IT. `HunterExternalSourceReview`'s view is
  // `<div class="shortlist-hunter-review">` around the SAME `<shortlist-extension-alerts …
  // force-slim="::true" flex-basis: Slim>` and the same named side view — it differs only in
  // the extra external-source bindings it passes that component and in `?externalSource`,
  // which selects which source the list is drawn from. With no `externalSource` in the URL, as
  // in the reference capture, the source IS the tenant's own ATS and the screen is the review
  // screen. Giving it a screen of its own would duplicate 400 lines to render the same pixels.
  'shortlist/external-source-review': <ShortlistReview />,
  // Phase 4's first screen, which exists this early because the Phase-3 components were built
  // over the tab GROUP rather than over "candidates" — see ClientContactReview for what did
  // and did not carry over.
  'client-contact/review': <ClientContactReview />,
  'client-contact/client-contact/profile': <ClientContactProfile />,
  // The same profile inside the review screen's side view. Upstream declares it as a separate
  // state with its own view and the same snippet — the difference is the alert behind it, and
  // `viaHunter` is what carries that: see the prop, which decides the fetch, not just the icons.
  'client-contact/review/client-contact/profile': <ClientContactProfile viaHunter />,
  // Its side-view children. The composer and the sign-in panel already serve both products;
  // `more` targets the transcluded view and, as on the candidate profile, does not open without
  // an `algoScore` in the URL — so it stays PendingSlice and the company machinery it would
  // render is not owed here.
  'client-contact/client-contact/profile/client-contact-messages': (
    <CandidateMessagesRoute
      entity="clientContacts"
      closePath="/chrome-extension/extension/client-contact/client-contact/profile"
    />
  ),
  'client-contact/client-contact/profile/email-signin': <EmailSignInRoute />,
  // The contact's "More" — the companies behind them, where the candidate's is a CV. Both
  // places it renders, since the review twin transcludes the same view.
  'client-contact/client-contact/profile/more': <ClientContactPreviewRoute />,
  'client-contact/review/client-contact/profile/more': <ClientContactPreviewRoute />,
  // The export panel, in the review screen's side view.
  'shortlist/review/export': <ExportPanelRoute />,
  // The same panel for contacts: one template, one method call's difference. See ExportPanelRoute.
  'client-contact/review/export': <ExportPanelRoute entity="clientContacts" />,
  // SINGLE_PROFILE_EXPORT's list picker on the STANDALONE profile (cee70a51e8) — the review's
  // panel with no hunter behind it; the profile runs the export. See ProfileExportRoute.
  'shortlist/candidate/profile/export': <ProfileExportRoute />,
  'client-contact/client-contact/profile/export': <ProfileExportRoute entity="clientContacts" />,
  // "Contact us". Both products render the same form; nothing in it varies by entity.
  'shortlist/contact': <ExtensionContact maxWidth={ExtensionViewWidth.Slim} />,
  'client-contact/contact': <ExtensionContact maxWidth={ExtensionViewWidth.Slim} />,
  // The not-found screen, which both products share. `ProfileNotFound` takes the entity because
  // the word in its message differs ("Candidate" / "Contact"); everything else is identical.
  'shortlist/profile-not-found': <ProfileNotFound entity="candidates" flexBasis={ExtensionViewWidth.Extended} />,
  'client-contact/profile-not-found': (
    <ProfileNotFound entity="clientContacts" flexBasis={ExtensionViewWidth.Extended} />
  ),
  // One candidate's profile, opened by id with no alert behind it. Its twin under
  // `review/candidate/profile` is the same snippet with an alert, and arrives with the
  // matching-summary card that only an alert can produce.
  'shortlist/candidate/profile': <CandidateProfile />,
  // The twin with an alert behind it, in the review screen's side view. See `viaHunter`: it
  // selects the alert-scoped fetch, asks for the matching summary, and adds Favor and Archive.
  'shortlist/review/candidate/profile': <CandidateProfile viaHunter />,
  // "Please connect your work email", in whichever side view it was opened from. The profile's
  // copy gets no close target: upstream binds no `alert-id` there and so renders no ✕.
  'shortlist/candidate/profile/email-signin': <EmailSignInRoute />,
  // "Add <someone> to Bullhorn job". Under the profile the panel's heading names the one
  // candidate — the state seeds the checkbox container with it — and under review it names the
  // list selection, which the container still owns and which is not ported yet.
  'shortlist/candidate/profile/add-to-job': (
    <AddToJobRoute closePath="/chrome-extension/extension/shortlist/candidate/profile" />
  ),
  'shortlist/review/add-to-job': <AddToJobRoute closePath="/chrome-extension/extension/shortlist/review" />,
  // The email composer. Its send path is deliberately not wired — see MessageComposer.
  'shortlist/candidate/profile/candidate-messages': (
    <CandidateMessagesRoute closePath="/chrome-extension/extension/shortlist/candidate/profile" />
  ),
  'shortlist/review/candidate-messages': (
    <CandidateMessagesRoute closePath="/chrome-extension/extension/shortlist/review" />
  ),
  // The contacts composer renders the SHARED entity template, which is this one with
  // `Candidate` renamed to `Entity` and a daily-send-limit line added. See MessageComposer.
  'client-contact/review/client-contact-messages': (
    <CandidateMessagesRoute entity="clientContacts" closePath="/chrome-extension/extension/client-contact/review" />
  ),
  // The "More" view — an iframe over the candidate's CV, transcluded INSIDE the snippet. It
  // renders only when `isInViewMoreState()` holds, which needs an `algoScore` in the URL: the
  // references for both of these used to be captured without one and so held the CLOSED state,
  // which is what `fixtures.json`'s `queryDefaults` now supplies.
  'shortlist/candidate/profile/more': <CandidateResumeRoute />,
  // The same iframe inside the review screen's side view. It was the one child of the review
  // profile still resolving to `PendingSlice`, and with the reference finally OPEN that showed
  // up as 19.41% at Full — the whole right half of the screen.
  'shortlist/review/candidate/profile/more': <CandidateResumeRoute />,
  'shortlist/review/email-signin': (
    <EmailSignInRoute closePath="/chrome-extension/extension/shortlist/review" />
  ),
  'client-contact/review/email-signin': (
    <EmailSignInRoute closePath="/chrome-extension/extension/client-contact/review" />
  ),
  // The candidate criteria builder, in its full-view frame. See EditAlert.
  //
  // ⚠️ `client-contact/edit-alert` IS NOT THIS SCREEN, and it used to be mapped here on the
  // stated grounds that "upstream renders the same page for both". It does not:
  // `extension-alert-edit.tpl.html` picks `<chrome-extension-search-page>` or
  // `<client-contact-search-page>` on the product, and the contact form is a separate
  // 1033-line template whose sections are Contact filters, Company filters, Location, Company
  // location and ATS filters — none of the candidate form's nine. Serving the candidate
  // builder there renders a form for the wrong entity, which is worse than rendering nothing,
  // so the route falls back to `PendingSlice` until `client-contact-search-form/` is ported.
  'shortlist/edit-alert': <EditAlert />,
  'client-contact/edit-alert': <ClientContactEditAlert />,
}

/**
 * The paths that render INSIDE another screen rather than instead of it.
 *
 * ⚠️ `review/export` IS NOT A SIBLING OF `review`, IT IS A CHILD OF IT. Upstream's states are a
 * tree and these declare `views: { 'shortlist-hunter-review-side-view@…': … }` — they fill a
 * NAMED VIEW inside the review screen, which keeps rendering underneath. A flat table maps them
 * to a screen of their own and the candidate list disappears the moment one opens, which is a
 * whole panel of difference no test sees because both render something.
 *
 * Nesting them means the parent supplies an `<Outlet/>` where its side view is. The children
 * whose panel has not been ported still resolve to `PendingSlice`, which renders nothing — and
 * `.shortlist-hunter-review-side-view` is zero-width without the `rendered` class, so an
 * unported child leaves the screen exactly as the reference for the PARENT route has it.
 */
/** A side-view child: a path, or a path that is itself a parent of further paths. */
type SideViewChild = string | readonly [string, readonly string[]]

const SIDE_VIEW_PARENTS: ReadonlyArray<readonly [string, readonly SideViewChild[]]> = [
  [
    'shortlist/review',
    [
      'shortlist/review/add-to-job',
      'shortlist/review/candidate-messages',
      // Both a child of the review and the parent of its own `more` — see the contact side.
      ['shortlist/review/candidate/profile', ['shortlist/review/candidate/profile/more']],
      'shortlist/review/email-signin',
      'shortlist/review/export',
    ],
  ],
  // ⚠️ THE PROFILE'S FIVE CHILDREN DO NOT SHARE ONE HOLE. `more` fills
  // `shortlist-candidate-profile-view@…`, which the snippet TRANSCLUDES and which widens it to
  // Extended + Detailed; the other three fill `shortlist-candidate-profile-side-view@…`, a
  // sibling box beside it. Both are children of this route all the same, so they nest here and
  // `CandidateProfile` routes the outlet to the right one by path.
  [
    'shortlist/candidate/profile',
    [
      'shortlist/candidate/profile/add-to-job',
      'shortlist/candidate/profile/candidate-messages',
      'shortlist/candidate/profile/email-signin',
      'shortlist/candidate/profile/export',
      'shortlist/candidate/profile/more',
    ],
  ],
  // The external-source review's side view, same shape as the hunter review's. Its own
  // reference capture shows the parent screen with the side view EMPTY: the panel is gated on
  // `candidateExternalId`, which the URL does not carry, so upstream renders nothing there
  // either. Nesting is what lets our unported child do the same — flat, it would replace the
  // screen with a blank.
  [
    'shortlist/external-source-review',
    [
      'shortlist/external-source-review/candidate/external-source-profile',
      'shortlist/external-source-review/external-source-add-to-job',
    ],
  ],
  [
    'client-contact/client-contact/profile',
    [
      'client-contact/client-contact/profile/client-contact-messages',
      'client-contact/client-contact/profile/email-signin',
      'client-contact/client-contact/profile/export',
      'client-contact/client-contact/profile/more',
    ],
  ],
  [
    'client-contact/review',
    [
      'client-contact/review/client-contact-messages',
      // ⚠️ THE PROFILE IS BOTH — A CHILD OF THE REVIEW AND THE PARENT OF ITS OWN `more`, which
      // is what upstream's tree says and what the screen needs. Listed flat, `/more` replaced
      // the profile instead of opening inside it: the review's side view held nothing at all
      // while the reference has the contact's snippet sitting in it, and the same shape would
      // have been wrong on the candidate side beside it.
      ['client-contact/review/client-contact/profile', ['client-contact/review/client-contact/profile/more']],
      'client-contact/review/email-signin',
      'client-contact/review/export',
    ],
  ],
]

/**
 * One child, with the grandchildren it has if it is itself a parent.
 *
 * The nesting stops at two levels on purpose: upstream goes no deeper on these screens, and a
 * general tree here would be a builder nobody can read for a shape nothing uses.
 */
function nest(parent: string, child: SideViewChild): RouteObject {
  const [path, grandchildren] = typeof child === 'string' ? ([child, []] as const) : child
  return {
    // Relative to the parent, which is what React Router nests on.
    path: path.slice(parent.length + 1),
    element: SERVED[path] ?? <PendingSlice />,
    ...(grandchildren.length === 0
      ? {}
      : { children: grandchildren.map((grandchild) => nest(path, grandchild)) }),
  }
}

function pathOf(child: SideViewChild): string {
  return typeof child === 'string' ? child : child[0]
}

const NESTED_PATHS = new Set(
  SIDE_VIEW_PARENTS.flatMap(([parent, children]) => [
    parent,
    ...children.flatMap((child) =>
      typeof child === 'string' ? [child] : [child[0], ...child[1].map((grandchild) => pathOf(grandchild))],
    ),
  ]),
)

export const extensionRoutes: RouteObject[] = [
  ...[...SHORTLIST_PATHS, ...CLIENT_CONTACT_PATHS]
    .filter((path) => !NESTED_PATHS.has(path))
    .map((path) => ({ path, element: SERVED[path] ?? <PendingSlice /> })),
  ...SIDE_VIEW_PARENTS.map(([parent, children]) => ({
    path: parent,
    element: SERVED[parent] ?? <PendingSlice />,
    children: children.map((child) => nest(parent, child)),
  })),
]
